import React, { useEffect, useRef, useCallback } from 'react';
import { Cartographic, Math as CesiumMath } from 'cesium';
import { ZoomIn, ZoomOut } from 'lucide-react';
import CesiumNavigation from 'cesium-navigation-es6';
import { useCesium } from '@/context/CesiumContext';
import { relativeZoom } from '@/helper/zoomHelper';

export const MapNavigationControls: React.FC = () => {
	const navigationRef = useRef<any>(null);
	const { viewer } = useCesium();

	useEffect(() => {
		if (!viewer || navigationRef.current) return;

		navigationRef.current = new CesiumNavigation(viewer, {
			defaultResetView: new Cartographic(
				CesiumMath.toRadians(8.380251),
				CesiumMath.toRadians(49.63142),
				155.91
			),
			duration: 3,
			enableCompass: true,
			enableZoomControls: false,
			enableDistanceLegend: false,
			enableCompassOuterRing: true,
			// cesium-navigation-es6 renders its own tooltip DOM once, outside React; it does
			// not react to a language switch, so this stays untranslated (i18n task 8).
			resetTooltip: 'Reset View',
		});

		return () => {
			navigationRef.current?.destroy?.();
			navigationRef.current = null;
		};
	}, [viewer]);

	if (!viewer) return null;

	const handleZoomIn = () => {
		relativeZoom(viewer, 0.5);
	};

	const handleZoomOut = () => {
		relativeZoom(viewer, 2);
	};

	return (
		<div className="absolute right-6 top-60 -translate-y-1/2 z-[1] select-none">
			<div
				className="flex flex-col rounded-xl shadow-xl overflow-hidden"
				style={{
					background: 'rgba(47, 53, 60, 0.8)',
					backdropFilter: 'blur(8px)',
					border: '1px solid rgba(255, 255, 255, 0.4)',
				}}
			>
				<ZoomButton icon={<ZoomIn className="w-4 h-4" />} onClick={() => handleZoomIn()} />

				<div
					style={{
						height: '1px',
						background: 'rgba(255, 255, 255, 0.4)',
					}}
				/>

				<ZoomButton icon={<ZoomOut className="w-4 h-4" />} onClick={() => handleZoomOut()} />
			</div>
		</div>
	);
};

interface ZoomButtonProps {
	onClick: () => void;
	icon: React.ReactNode;
}

const ZoomButton: React.FC<ZoomButtonProps> = ({ onClick, icon }) => (
	<button
		onClick={onClick}
		className="
      h-9 w-9
      flex items-center justify-center
      text-white
      transition-all
      hover:bg-white/10
      active:scale-95
    "
	>
		{icon}
	</button>
);
